import { styled, Box, useTheme, Theme, Typography } from "@mui/material"
import { ComponentPropsWithoutRef, ElementType, useMemo, useState } from "react"
import { plateCountryCodeMapper } from "@tm/utils"
import { LicensePlateComponentInitiator } from "@tm/models"
import { useLocalization } from "@tm/localization"
import { TextField } from "../../muiStyled/Inputs/textfield"
import IconCh from "./icons/ch.svg"
import IconN from "./icons/n.svg"
import IconEu from "./icons/eu.svg"
import IconUk from "./icons/uk.svg"

export type LicensePlateProps = {
    value?: string
    countryCode?: string
    size?: "extrasmall" | "small" | "medium" | "large" | "extralarge"
    isReadOnly?: boolean
    /**
     * This prop should be used when a local state needs to be up to date
     * with the input state stored in the generic component
     */
    onChange?: (plateId: string) => void
    /**
     * This prop should be used when an api call should happen e.g. on `Enter` key press or `onBlur`
     */
    onChangeConfirm?(plateId: string): void
    licensePlateComponentInitiator?: LicensePlateComponentInitiator
    width?: string
} & Omit<ComponentPropsWithoutRef<"input" | "textarea">, "size" | "value" | "onChange">

function LicensePlateComponent(props: LicensePlateProps) {
    const { value, countryCode, className, style, isReadOnly, size, onChangeConfirm, onChange, disabled, required, maxLength } = props
    const [plateValue, setPlateValue] = useState(value || "")

    const showCountryCode = useMemo(() => {
        if (!countryCode) {
            return false
        }
        if (props.countryCode === undefined) {
            return true
        }
        return !!props.countryCode
    }, [countryCode, props.countryCode])

    const theme = useTheme()
    const { translateText } = useLocalization()

    const plateStyle = getPlateStyle(countryCode)
    const plateCountryCode = plateCountryCodeMapper(countryCode || "")
    const placeholder = translateText(12443).toLocaleUpperCase()
    const hasError = required && plateValue.length === 0

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const plateId = e.target.value
        setPlateValue(plateId)
        onChange?.(plateId)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            onChangeConfirm?.(plateValue)
        }
    }

    const handleBlur = () => {
        onChangeConfirm?.(plateValue)
    }

    const renderPlaceholder = () => {
        return (
            <Typography variant="body2" fontFamily="EuroPlate" color={theme.palette.grey[700]}>
                {placeholder}
            </Typography>
        )
    }

    return (
        <Box className={className} style={style} border={`1px solid ${hasError ? theme.palette.error.main : "#a0a0a0"}`}>
            <Box className="country">
                {showCountryCode && (
                    <>
                        <CountryIcon countryCode={countryCode} />
                        {plateStyle !== "CH" && <Box className="country-code">{plateCountryCode}</Box>}
                    </>
                )}
            </Box>
            {(isReadOnly === undefined || isReadOnly === true) && <Box className="value">{value || renderPlaceholder()}</Box>}
            {isReadOnly === false && (
                <TextField
                    sx={{
                        border: "none",
                        marginTop: 0.5,
                        ".MuiInputBase-input": {
                            alignSelf: "center",
                            fontFamily: "EuroPlate",
                            letterSpacing: "normal",
                            fontSize: getFontSize(size),
                            color: theme.palette.common.black,
                        },
                    }}
                    className="value"
                    placeholder={placeholder}
                    value={plateValue}
                    variant="standard"
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    errorHandling={[{ errorMessage: hasError ? translateText(37) : "" }]}
                    disabled={disabled}
                    inputProps={{ maxLength }}
                />
            )}
        </Box>
    )
}

function isCountryInEU(countryCode: string) {
    switch (countryCode.toUpperCase()) {
        case "A":
        case "AT":
        case "B":
        case "BE":
        case "BG":
        case "CY":
        case "CZ":
        case "D":
        case "DE":
        case "DK":
        case "E":
        case "EE":
        case "ES":
        case "EST":
        case "F":
        case "FI":
        case "FIN":
        case "FR":
        case "GR":
        case "H":
        case "HR":
        case "HU":
        case "I":
        case "IE":
        case "IRL":
        case "IT":
        case "L":
        case "LT":
        case "LU":
        case "LV":
        case "M":
        case "MT":
        case "NL":
        case "P":
        case "PL":
        case "PT":
        case "RO":
        case "S":
        case "SE":
        case "SI":
        case "SK":
        case "SLO":
            return true
        default:
            return false
    }
}

function CountryIcon({ countryCode }: { countryCode?: string }) {
    const plateStyle = getPlateStyle(countryCode || "")
    switch (plateStyle) {
        case "CH":
            return <IconCh className="icon" />
        case "N":
            return <IconN className="icon" />
        case "UK":
            return <IconUk className="icon" />
        case "NL":
        case "EU":
            return <IconEu className="icon" />
        default:
            return null
    }
}

function getCountryCodeFontSize(plateStyle: PlateStyles) {
    if (plateStyle === "Unknown") {
        return "0.75em"
    }
    return "0.5em"
}

function getCountryWidth(plateStyle: PlateStyles, licensePlateComponentInitiator?: LicensePlateComponentInitiator) {
    if (plateStyle === "CH") {
        switch (licensePlateComponentInitiator) {
            case LicensePlateComponentInitiator.VehicleEditor: {
                return "2em"
            }
            case LicensePlateComponentInitiator.VehicleWidget: {
                return "1.5em"
            }
            default:
                return "1.2em"
        }
    } else if (plateStyle === "Unknown") {
        switch (licensePlateComponentInitiator) {
            case LicensePlateComponentInitiator.VehicleEditor: {
                return "1.8em"
            }
            default:
                return "1.5em"
        }
    } else {
        return "1.2em"
    }
}

function getFontSize(size?: "extrasmall" | "small" | "medium" | "large" | "extralarge") {
    switch (size) {
        case "extrasmall":
            return "0.8em"

        case "small":
            return "0.9em"

        case "large":
            return "1.2em"

        case "extralarge":
            return "1.5em"

        case "medium":
        default:
            return "1em"
    }
}

function getIconPadding(plateStyle: PlateStyles, licensePlateComponentInitiator?: LicensePlateComponentInitiator) {
    let padding: number | string

    if (
        plateStyle === "N" ||
        plateStyle === "UK" ||
        licensePlateComponentInitiator === LicensePlateComponentInitiator.PersonalData ||
        licensePlateComponentInitiator === LicensePlateComponentInitiator.VehicleInfoCrmMicro
    ) {
        padding = "0.1em"
    } else {
        padding = 0
    }

    return padding
}

const LicensePlate: ElementType<LicensePlateProps> = styled(LicensePlateComponent)<LicensePlateProps>(({
    size,
    countryCode,
    theme,
    licensePlateComponentInitiator,
    width,
}) => {
    const plateStyle = getPlateStyle(countryCode || "")

    const iconPadding = getIconPadding(plateStyle, licensePlateComponentInitiator)

    return {
        background: plateStyle === "NL" ? "#fdd504" : "#FBFBFB",
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.common.black,
        display: "inline-flex",
        fontSize: getFontSize(size),
        minWidth: "4em",
        width: width || "auto",
        lineHeight: 1,
        whiteSpace: "nowrap",
        height: "1.5em",
        verticalAlign: "text-bottom",
        flexShrink: 0,
        ".country": {
            display: "flex",
            flexDirection: "column",
            color: plateStyle === "Unknown" ? theme.palette.common.black : theme.palette.common.white,
            textTransform: "uppercase",
            borderRadius: `${theme.shape.borderRadius} 0 0 ${theme.shape.borderRadius}`,
            background: getPlateCountryBackground(plateStyle, theme),
            width: getCountryWidth(plateStyle, licensePlateComponentInitiator),
        },
        ".country-code": {
            fontSize: getCountryCodeFontSize(plateStyle),
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: plateStyle === "Unknown" ? "center" : "end",
            padding: "0.3em",
        },
        ".icon": {
            height: "100%",
            width: "1em",
            marginInline: "auto",
            display: "flex",
            alignItems: "center",
            marginTop: "0.1em",
            padding: iconPadding,
        },
        ".value": {
            padding: "0 0.3em",
            alignSelf: "center",
            fontFamily: "EuroPlate",
            letterSpacing: "normal",
        },
    }
})

export { LicensePlate }

type PlateStyles = "EU" | "CH" | "N" | "NL" | "UK" | "Unknown"

function getPlateStyle(countryCode?: string): PlateStyles {
    if (countryCode && isCountryInEU(countryCode) && countryCode.toLowerCase() !== "nl") {
        return "EU"
    }

    switch (countryCode?.toLowerCase()) {
        case "no":
        case "n":
            return "N"
        case "ch":
            return "CH"
        case "nl":
            return "NL"
        case "uk":
        case "gb":
            return "UK"
        default:
            return "Unknown"
    }
}

function getPlateCountryBackground(plateStyle: PlateStyles, theme: Theme) {
    switch (plateStyle) {
        case "Unknown": {
            return theme.palette.grey[400]
        }
        case "CH": {
            return "#FBFBFB"
        }
        default:
            return "#003bb2"
    }
}
